'use client';
/* eslint-disable @typescript-eslint/naming-convention */
import { ChevronDown, ChevronUp } from '@ps/web-fusion-icons';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { ReactNode } from 'react';
import type { DisclosureProps } from 'react-aria-components';
import { Button, Disclosure, DisclosurePanel } from 'react-aria-components';

import Icon from '../Icon/Icon';
import { useFusionTheme } from '../hooks';

import styles from './Accordion.module.scss';

export type AccordionProps = Exclude<DisclosureProps, 'style'> &
  VariantProps<typeof accordionVariants> & {
    icon?: React.FC<React.SVGProps<SVGElement>>;
    headingText: string;
    children: ReactNode;
  };

const accordionVariants = cva(styles.accordionWrapper, {
  variants: {
    variant: {
      contained: styles.contained,
      'non-contained': styles.nonContained
    }
  }
});

export function Accordion({
  icon,
  headingText,
  variant = 'contained',
  children,
  className,
  ...props
}: AccordionProps) {
  const { addThemeClassName } = useFusionTheme();

  return (
    <Disclosure className={addThemeClassName(styles.disclosureWrapper, className)} {...props}>
      {({ isExpanded, isDisabled }) => (
        <div className={accordionVariants({ variant })}>
          <Button slot="trigger" className={styles.headingContainer}>
            {icon && <Icon className={styles.icon} icon={icon} />}
            <div className={styles.headingContent}>
              <span className={styles.headingText}>{headingText}</span>
            </div>
            <Icon
              icon={isExpanded && !isDisabled ? ChevronUp : ChevronDown}
              className={styles.icon}
            />
          </Button>
          <DisclosurePanel className={styles.disclosurePanel}>{children}</DisclosurePanel>
        </div>
      )}
    </Disclosure>
  );
}

export default Accordion;
